import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from "react";
import styles from "./Toast.module.css";

type Tipo = "info" | "warning" | "error" | "success";
interface ToastItem {
  id: number;
  tipo: Tipo;
  texto: string;
}

interface Ctx {
  show: (tipo: Tipo, texto: string) => void;
}

const ToastCtx = createContext<Ctx | null>(null);

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<ToastItem[]>([]);

  const show = useCallback((tipo: Tipo, texto: string) => {
    const id = Date.now() + Math.random();
    setToasts((t) => [...t, { id, tipo, texto }]);
    setTimeout(() => {
      setToasts((t) => t.filter((x) => x.id !== id));
    }, 5500);
  }, []);

  return (
    <ToastCtx.Provider value={{ show }}>
      {children}
      <div className={styles.stack} aria-live="polite">
        {toasts.map((t) => (
          <div key={t.id} className={`${styles.toast} ${styles[t.tipo]}`}>
            {t.texto}
          </div>
        ))}
      </div>
    </ToastCtx.Provider>
  );
}

export function useToast(): Ctx {
  const ctx = useContext(ToastCtx);
  if (!ctx) {
    return {
      show: (_tipo, texto) => {
        console.warn("[toast fallback]", texto);
      },
    };
  }
  return ctx;
}

/** Bridge module-level -> React: permite emitToast fora de componentes. */
let pendingToasts: { tipo: Tipo; texto: string }[] = [];
const listeners = new Set<(t: { tipo: Tipo; texto: string }) => void>();

export function emitToast(tipo: Tipo, texto: string) {
  if (listeners.size === 0) {
    pendingToasts.push({ tipo, texto });
  } else {
    listeners.forEach((fn) => fn({ tipo, texto }));
  }
}

export function ToastBridge() {
  const { show } = useToast();
  useEffect(() => {
    const fn = (t: { tipo: Tipo; texto: string }) => show(t.tipo, t.texto);
    listeners.add(fn);
    if (pendingToasts.length > 0) {
      pendingToasts.forEach(fn);
      pendingToasts = [];
    }
    return () => {
      listeners.delete(fn);
    };
  }, [show]);
  return null;
}
